'use client';

import React from 'react';
import { evaluatePasswordStrength, PasswordStrengthResult } from '@/lib/auth-errors';

interface PasswordChecklistProps {
  password?: string;
  /**
   * If true, always shows the checklist (even when password input is empty). Defaults to true.
   */
  alwaysShow?: boolean;
  /**
   * Optional custom container className
   */
  className?: string;
  /**
   * Optional custom title
   */
  title?: string;
}

export default function PasswordChecklist({
  password = '',
  alwaysShow = true,
  className = '',
  title = 'Password requirements',
}: PasswordChecklistProps) {
  // If explicitly requested to hide when empty and password has length 0
  if (!alwaysShow && (!password || password.length === 0)) {
    return null;
  }

  const pwd = password || '';
  const strength: PasswordStrengthResult = evaluatePasswordStrength(pwd);
  const metCount = strength.checks.filter((c) => c.met).length;

  return (
    <div
      className={`password-checklist-wrapper ${className}`}
      role="region"
      aria-label="Password strength and requirements checklist"
      style={{
        marginTop: '10px',
        padding: '12px 14px',
        background: 'var(--elevated, #f4f4f6)',
        border: '1px solid var(--border, #e8e8ed)',
        borderRadius: '12px',
        fontSize: '12px',
      }}
    >
      {/* Header with strength score label and counter */}
      <div
        className="password-strength-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px',
        }}
      >
        <div
          className="password-strength-badge"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            fontSize: '12px',
            fontWeight: 700,
          }}
        >
          <span
            className="password-strength-dot"
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              display: 'inline-block',
              backgroundColor: pwd ? strength.color : '#9ca3af',
              boxShadow: pwd ? `0 0 6px ${strength.color}88` : 'none',
              transition: 'background-color 0.25s ease',
            }}
          />
          <span
            style={{
              color: pwd ? strength.color : 'var(--text-secondary, #6b7280)',
              fontWeight: 700,
            }}
          >
            {pwd ? strength.label : 'Strength: Enter password'}
          </span>
        </div>
        <span
          className="password-strength-count"
          style={{
            fontSize: '11px',
            color: 'var(--text-muted, #9ca3af)',
            fontWeight: 600,
          }}
        >
          {metCount} of {strength.checks.length} met
        </span>
      </div>

      {/* 5-segment progress bar */}
      <div
        className="password-strength-bars"
        aria-hidden="true"
        style={{
          display: 'flex',
          gap: '5px',
          marginBottom: '10px',
        }}
      >
        {[1, 2, 3, 4, 5].map((lvl) => {
          const isActive = pwd.length > 0 && strength.score >= lvl;
          return (
            <div
              key={lvl}
              className={`password-strength-segment ${isActive ? 'active' : ''}`}
              style={{
                flex: 1,
                height: '4px',
                borderRadius: '2px',
                backgroundColor: isActive ? strength.color : 'rgba(0, 0, 0, 0.08)',
                transition: 'background-color 0.25s ease',
              }}
            />
          );
        })}
      </div>

      {/* Checklist section */}
      <div
        className="password-checklist-title"
        style={{
          fontSize: '11px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-secondary, #6b7280)',
          marginBottom: '8px',
        }}
      >
        {title}:
      </div>

      <ul
        className="password-checklist-grid"
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '6px 12px',
        }}
      >
        {strength.checks.map((check) => (
          <li
            key={check.id}
            className={`password-check-item ${check.met ? 'met' : 'unmet'}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '11.5px',
              lineHeight: 1.35,
              color: check.met ? 'var(--text, #0a0a0b)' : 'var(--text-muted, #9ca3af)',
              fontWeight: check.met ? 600 : 500,
              transition: 'color 0.2s ease',
            }}
          >
            <span
              className={`password-check-icon ${check.met ? 'met' : 'unmet'}`}
              aria-hidden="true"
              style={{
                width: '16px',
                height: '16px',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                background: check.met ? 'rgba(22, 163, 74, 0.16)' : 'rgba(0, 0, 0, 0.05)',
                border: check.met ? '1px solid #16a34a' : '1px solid var(--border, #d1d5db)',
                color: check.met ? '#16a34a' : 'var(--text-muted, #9ca3af)',
                transition: 'all 0.2s ease',
              }}
            >
              {check.met ? (
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <span
                  style={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    background: 'var(--text-muted, #9ca3af)',
                    display: 'inline-block',
                  }}
                />
              )}
            </span>
            <span className="password-check-text" style={{ flex: 1 }}>
              {check.label}
            </span>
          </li>
        ))}
      </ul>

      {/* Status feedback message */}
      {strength.score >= 4 ? (
        <div
          className="password-checklist-hint success"
          style={{
            marginTop: '10px',
            padding: '7px 10px',
            borderRadius: '8px',
            fontSize: '11px',
            lineHeight: 1.4,
            background: 'rgba(22, 163, 74, 0.1)',
            border: '1px solid rgba(22, 163, 74, 0.25)',
            color: '#16a34a',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 600,
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span>Strong password! Your account is safe & secure.</span>
        </div>
      ) : pwd.length > 0 && strength.score < 3 ? (
        <div
          className="password-checklist-hint warning"
          style={{
            marginTop: '10px',
            padding: '7px 10px',
            borderRadius: '8px',
            fontSize: '11px',
            lineHeight: 1.4,
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
            color: '#d97706',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 600,
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>Add uppercase, lowercase, numbers, and symbols to strengthen.</span>
        </div>
      ) : null}
    </div>
  );
}
